import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useSound from 'use-sound';
import { HookOptions } from 'use-sound/dist/types';
import { b64toBlob } from './b64toBlob';
import { GlobalContext } from './GlobalContext';

export function useAudio({
    text,
    phoneme,
    audioFile,
    useToggle,
}: {
    text?: string | null;
    phoneme?: string | null;
    audioFile?: string;
    useToggle?: boolean;
}) {
    const speakingRate = useSelector((state: ReduxStore) =>
        parseFloat(state.authentication?.user?.speaking_rate || '1')
    );

    // Is used to trigger effect hook that checks if the request time is newer then the previous one
    const [triggerUrl, setTriggerUrl] = useState({
        url: '',
        requestTime: 0,
    });

    // The last time an api call was requested
    const [previousRequestTime, setPreviousRequestTime] = useState(0);

    // The main url that get's called
    const [soundUrl, setSoundUrl] = useState('');

    // global toggle variable that can be used to trigger this hook in an alternative way.
    const { toggleState } = useContext(GlobalContext);

    // Checks if the request time of an api call is newer then the previous one.
    // (prevent bug where response of older request overwrites the one from a newer request)
    useEffect(() => {
        // new request is older then the current one
        if (previousRequestTime > triggerUrl.requestTime) return;

        setPreviousRequestTime(triggerUrl.requestTime);
        setSoundUrl(triggerUrl.url);
    }, [triggerUrl]);

    const effectCondition = useToggle ? toggleState : text || audioFile;

    // Hook that creates blob and requests soundURL to be set
    useEffect(() => {
        const requestTime = new Date().getTime();

        if (audioFile) {
            axios
                .get(`${audioFile}`, {
                    responseType: 'blob',
                })
                .then((response) => {
                    const urlResult = window.URL.createObjectURL(response.data);
                    setTriggerUrl({ url: urlResult, requestTime });
                });
            return;
        }

        if (text) {
            const input = phoneme
                ? {
                      ssml: `<speak><phoneme alphabet="x-sampa" ph='${phoneme}'>${text}</phoneme> </speak>`,
                  }
                : {
                      text,
                  };

            axios
                .post(
                    'https://texttospeech.googleapis.com/v1beta1/text:synthesize?key=AIzaSyCJvhqqXiHljFhTjTrVDVZtSrYbQcxshRM',
                    {
                        input,
                        voice: {
                            languageCode: 'en-nl',
                            name: 'nl-NL-Wavenet-D',
                            ssmlGender: 'FEMALE',
                        },
                        audioConfig: {
                            audioEncoding: 'MP3',
                            speakingRate,
                            volumeGainDb: 6,
                        },
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    }
                )
                .then((response) => {
                    const blob = b64toBlob(
                        response.data.audioContent,
                        'audio/mpeg'
                    );

                    const urlResult = window.URL.createObjectURL(blob);
                    setTriggerUrl({ url: urlResult, requestTime });
                });
        }
    }, [effectCondition, speakingRate]);

    const [play, { stop, isPlaying, sound, duration }] = useSound(soundUrl, {
        format: 'mp3',
    } as HookOptions);

    const url = sound?._src;

    // stop playing when component unmounts
    useEffect(() => () => stop(), [url]);

    return { play, stop, isPlaying, url, duration };
}
